import { useEffect } from "react";
import { api } from "./api";

const QUARTERS = [25, 50, 75, 100] as const;
type Quarter = (typeof QUARTERS)[number];

/** How far down the page the bottom of the screen is, as a share of the page's height (0-100). */
export function readDepth(): number {
  const doc = document.documentElement;
  const height = Math.max(doc.scrollHeight, document.body?.scrollHeight ?? 0);
  if (height <= 0) return 0;
  return Math.min(100, ((window.scrollY + window.innerHeight) / height) * 100);
}

/**
 * Tells the API how far down their results a person reads (Oct 21 call: "we can track scroll
 * depth"), once per quarter passed and only the furthest of those at a time. Runs on the
 * taker's own results page, after it has rendered; the admin's read-only copy doesn't use it,
 * and the API ignores staff devices.
 */
export function useScrollDepth(takeId: string, resultKey: string | null | undefined, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let sent = 0;
    let frame = 0;
    const check = () => {
      frame = 0;
      const depth = readDepth();
      const passed = QUARTERS.filter((q) => depth >= q - 0.5).at(-1);
      if (passed && passed > sent) {
        sent = passed;
        api.sendScrollDepth(takeId, passed as Quarter, resultKey).catch(() => undefined);
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    // A page that fits on one screen has been read to the bottom already.
    const first = setTimeout(check, 1000);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      clearTimeout(first);
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [takeId, resultKey, enabled]);
}
